import React, {useCallback, useEffect, useMemo, useState} from "react"
import WhiteLogoImage from "../../assets/icons/ngs-white-blue.svg"
import {useParams, useSearchParams} from "react-router-dom";
import {apiV2, massWarningAPI} from "../../api/axiosConfig";
import {useQuery} from "react-query";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import {ButtonV2} from "../../components/ui/ButtonsV2/ButtonV2";
import clsx from "clsx";
import BasicText from "../../components/form-v2/basic-text";
import {formatPhoneNumber} from "../../lib/pnone/formatPhoneNumber";
import {isAxiosError} from "axios";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import {PhoneInput} from "../../components/form-v2/phone-input/PhoneInput";

const Layout: React.FC<React.PropsWithChildren> = (props) => {


    return (
        <div className={"flex flex-col items-center justify-start gap-8 text-grey bg-desaturated-grey min-h-screen"}>
            <Header/>
            <div className="w-full p-8 flex flex-col items-center gap-8">
                {props.children}
            </div>
        </div>
    )

}

const Header: React.FC = () => {


    return <div
        className="bg-white bg-gradient-to-r from-[#2D166F_50%] to-[#007584] h-28 w-full px-6 flex justify-center">
        <img
            src={WhiteLogoImage}
            alt="NGS Logo"
            className="h-fill w-auto max-h-30 max-w-[200px] object-contain"
        />
    </div>

}

type OptOutStatus = {
    optOut: boolean
}

async function fetchData(id: string) {
    const res = await apiV2.get<OptOutStatus>(`siren/opt-out/${id}`, {})
    return res.data;
}

type OptInStatus = {
    optOut: boolean
    sms: boolean
    contactPhone: string
    auroraPhone: string
    email: string
}


function Inner() {
    const {id} = useParams()

    const [loading, setLoading] = useState(false)

    const [error, setError] = useState<string>()

    const [mode, setMode] = useState<"email"|"mobile">("mobile")

    const [status, setStatus] = useState<OptInStatus>()
    const [email, setEmail] = useState<string>("")
    const [mobile, setMobile] = useState<string>("")

    const submit = useCallback(() => {
        setLoading(true)

        apiV2.get<OptInStatus>("siren/opt-in-status", {
            params: {query: mode == "mobile" ? mobile : email},
        }).then((res) => {
            setStatus(res.data)
        }).catch((err) => {
            if(isAxiosError(err) && err?.response?.data?.error) {
                setError(err?.response?.data?.error)
                return
            }
            setError("An error occurred.")
        }).finally(() => {
            setLoading(false)
        })

    }, [email, mode, mobile])


    if (error) {
        return <p className="text-center text-red">{error}</p>
    }

    if (status && !status.optOut){
        return (
            <p className="text-center">You are already opted in to receive Siren alerts.</p>
        )
    }

    if(status && !status.sms){
        return (
            <p className="text-center">An email has been sent to {status.email} with instruction on how to opt back in.</p>
        )
    }

    if (status && status.sms) {
        return (
            <p className="text-center">
                To opt back in to receiving Siren alerts please
                send <strong>START</strong> to <span className="whitespace-nowrap">{formatPhoneNumber(status.auroraPhone)}</span> from <span className="whitespace-nowrap">{status.contactPhone}</span>.
            </p>
        )
    }


    return (
        <>
            <div className="flex flex-row items-center justify-center gap-8">
                {/* <button className={mode != "email" ? "opacity-50" : ""} onClick={() => setMode("email")} >Email Address</button>*/}
                <button className={mode != "mobile" ? "opacity-50" : ""} onClick={() => setMode("mobile")} >Mobile Phone</button>
            </div>
            { mode == "email" && (<BasicText value={email} onChange={setEmail} placeholder={"Email Address"} />)}
            { mode == "mobile" && (<PhoneInput initialValue={mobile} onChange={setMobile} placeholder={"Phone number"} /> )}
            <ButtonV2 label={"Submit"} onClick={submit} loading={loading}/>
        </>
    )
}

type EmailOptInProps = {
    optInKey: string
}

function EmailOptIn({optInKey}: EmailOptInProps) {

    const [loading, setLoading] = useState(true)

    const [error, setError] = useState<string>()
    const [success, setSuccess] = useState<boolean>()

    useEffect(() => {

        apiV2.post("siren/opt-in-email",{key:optInKey}).catch((err) => {
            if(isAxiosError(err) && err?.response?.data?.error) {
                setError(err.response.data.error)
                return
            }

            return "An error occurred."

        }).finally(() => {
            setLoading(false)
        })


    }, [optInKey])

    if(error){
        return <p className="text-center">{error}</p>
    }


    if(loading){
        return (
            <div className="flex items-center justify-center">
                <LoadingSpinner/>
            </div>
        )
    }


    return (
        <p className="text-center">You have been opted back in to receiving Siren alerts.</p>
    )
}


export function SirenOptIn() {
    const [searchParams] = useSearchParams()
    const key = searchParams.get("key")
    if(key){
        return <Layout>
            <div className={"flex flex-col gap-4 w-[600px]"}>
                <EmailOptIn optInKey={key}/>
            </div>
        </Layout>
    }


    return (
        <Layout>
            <div className={"flex flex-col gap-4 w-[600px]"}>
                <Inner/>
            </div>
        </Layout>
    )
}